import { TimeRangeConfig, TimeRangeKind } from '@/api/generated/model';
import { Button } from '@/comps/primitives/button';
import { WithClassName, WithOnChange } from '@/types/component';
import { formatToLocaleDateValue, getTimezoneName } from '@/utils/date.util';
import { cn } from '@/utils/shad';
import { format } from 'date-fns';
import { CalendarDays, CalendarIcon, ChevronDown } from 'lucide-react';
import { useCallback, useState, useEffect } from 'react';
import { List, ListItem } from './list';
import { Calendar } from './primitives/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './primitives/popover';
import { TimePicker } from './primitives/time-picker';
import { StableTimestamp } from '@/providers/stable-timestamp.provider';
import { getDateTRangeFromKind, getDateTRangeFromKindWithoutCustom } from '@/utils/get-date-range-from-kind';

type Props = WithClassName &
  WithOnChange<TimeRangeConfig> & {
    defaultValue: TimeRangeConfig;
    ghost?: boolean;
    label?: string;
    disabled?: boolean;
  };

export function TimeRangePicker({ defaultValue, onChange, ghost, label, disabled }: Props) {
  const { getTimestamp } = StableTimestamp.useProvided();
  const stableNow = getTimestamp();
  const [selectedDate, setSelectedDate] = useState<
    { from: Date | undefined; to?: Date; syncToToday?: boolean } | undefined
  >();
  const [dateRange, setDateRange] = useState<TimeRangeConfig>(defaultValue);

  // Keep the button label in sync when the time range is changed externally
  // (e.g. deep-link auto-expanding to "Custom").
  useEffect(() => {
    setDateRange(defaultValue);
  }, [defaultValue]);
  const [isSelectingCustom, setIsSelectingCustom] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');

  const combineDateTime = useCallback((date: Date, time: string): Date => {
    const [hours, minutes] = time.split(':').map(Number);

    if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      console.warn(`Invalid time string: ${time}, falling back to 00:00`);
      return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    }

    const combined = new Date(date);
    combined.setHours(hours, minutes, 0, 0);
    return combined;
  }, []);

  const onSelectedDateChange = (
    selectedDate: { from: Date | undefined; to?: Date; syncToToday?: boolean } | undefined,
  ) => {
    setSelectedDate(selectedDate);
  };

  const alignTimeRangeToToday = (
    selectedDate: { from: Date | undefined; to?: Date; syncToToday?: boolean } | undefined,
  ) => {
    if (selectedDate?.from) {
      const startDateTime = combineDateTime(selectedDate.from, startTime);

      if (startDateTime >= combineDateTime(stableNow, '23:59')) {
        console.warn('Invalid date range: start date must be before end date');
        return;
      }

      const newDateRange: TimeRangeConfig = {
        start: formatToLocaleDateValue(startDateTime.valueOf()),
        timezone: getTimezoneName(),
        recompute: true,
        kind: TimeRangeKind.custom,
      };

      onDateRangeChange(newDateRange);
    }
  };

  useEffect(() => {
    if (dateRange?.start && dateRange?.end && dateRange.kind === 'custom') {
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);

      // Validate dates before processing
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.warn('Invalid date range detected, skipping time update');
        return;
      }

      const startTimeStr = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`;
      const endTimeStr = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;

      // Only update if the time has actually changed to prevent unnecessary re-renders
      setStartTime((prev) => (prev !== startTimeStr ? startTimeStr : prev));
      setEndTime((prev) => (prev !== endTimeStr ? endTimeStr : prev));
    }
  }, [dateRange]);

  const onDateRangeChange = useCallback(
    (dateRange: TimeRangeConfig) => {
      setDateRange(dateRange);
      setSelectedDate(undefined);
      setPopoverOpen(false);
      onChange?.(dateRange);
      setIsSelectingCustom(false);
    },
    [onChange],
  );

  const ranges = Object.entries(timerangeMap);

  return (
    <Popover onOpenChange={setPopoverOpen} open={popoverOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={ghost ? 'ghost' : 'outline'}
          className={cn(ghost && 'text-foreground hover:bg-transparent')}
          PrependIcon={CalendarDays}
          AppendIcon={ChevronDown}
          disabled={disabled}
        >
          {label ?? formatTimeRange(dateRange)}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-fit max-w-none bg-popover! p-0 pt-1.5"
        align="end"
        onInteractOutside={() => setIsSelectingCustom(false)}
      >
        {!isSelectingCustom && (
          <List className="w-50">
            {ranges
               
              .filter(([_, v]) => !!v)
              .map(([k, v]) => (
                <ListItem
                  className="mx-1.5 rounded font-normal transition-colors"
                  key={k}
                  onClick={() =>
                    onDateRangeChange(
                      k === TimeRangeKind.custom
                        ? getDateTRangeFromKind(dateRange, stableNow)
                        : getDateTRangeFromKindWithoutCustom(k as Exclude<TimeRangeKind, 'custom'>, stableNow),
                    )
                  }
                >
                  {v}
                </ListItem>
              ))}
            <hr className="mt-1" />
            <ListItem
              className="mx-1 my-1 rounded font-normal transition-colors"
              onClick={() => {
                setIsSelectingCustom(true);
              }}
            >
              Custom date
            </ListItem>
          </List>
        )}
        {isSelectingCustom && (
          <div className="p-4">
            <Calendar
              initialFocus
              mode="range"
              selected={selectedDate}
              onSelect={onSelectedDateChange}
              alignTimeRangeToToday={alignTimeRangeToToday}
              numberOfMonths={2}
            />

            {/* Time selection section below calendar - always visible */}
            <div className="mt-4 border-t pt-4">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground">Start time:</span>
                  <TimePicker value={startTime} onChange={(time) => setStartTime(time)} label="" />
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground">End time:</span>
                  <TimePicker value={endTime} onChange={(time) => setEndTime(time)} label="" />
                </div>
              </div>
            </div>

            {/* Apply/Cancel buttons */}
            <div className="mt-4 flex justify-end gap-2 border-t pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsSelectingCustom(false);
                  setSelectedDate(undefined);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (selectedDate?.from && selectedDate?.to) {
                    const startDateTime = combineDateTime(selectedDate.from, startTime);
                    const endDateTime = combineDateTime(selectedDate.to, endTime);

                    // Validate that start is before end
                    if (startDateTime >= endDateTime) {
                      console.warn('Invalid date range: start date must be before end date');
                      return;
                    }

                    const newDateRange: TimeRangeConfig = {
                      start: formatToLocaleDateValue(startDateTime.valueOf()),
                      end: formatToLocaleDateValue(endDateTime.valueOf()),
                      timezone: getTimezoneName(),
                      recompute: false,
                      kind: TimeRangeKind.custom,
                    };

                    onDateRangeChange(newDateRange);
                  } else if (selectedDate?.from && selectedDate?.syncToToday) {
                    const startDateTime = combineDateTime(selectedDate.from, startTime);

                    // Validate that start is before end
                    if (startDateTime >= combineDateTime(stableNow, endTime)) {
                      console.warn('Invalid date range: start date must be before end date');
                      return;
                    }

                    const newDateRange: TimeRangeConfig = {
                      start: formatToLocaleDateValue(startDateTime.valueOf()),

                      timezone: getTimezoneName(),
                      recompute: true,
                      kind: TimeRangeKind.custom,
                    };

                    onDateRangeChange(newDateRange);
                  }
                }}
                disabled={!selectedDate?.from || (!selectedDate?.to && !selectedDate?.syncToToday)}
              >
                Apply
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export const formatTimeRange = (timeRange: TimeRangeConfig) => {
  if (!timeRange) return 'n/a';

  const formatDateTime = (dateStr: string, showTime: boolean) => {
    const date = new Date(dateStr);
    if (showTime) {
      return format(date, 'MMM dd, HH:mm');
    }
    return format(date, 'MMM dd');
  };

  if (timeRange.kind === 'custom') {
    if (timeRange.start && timeRange.end) {
      return `${formatDateTime(timeRange.start, true)} - ${formatDateTime(timeRange.end, true)}`;
    }

    if (timeRange.start) {
      const endDate = new Date(timeRange.end ?? Date.now());
      const displayEndTime = `${format(endDate, 'MMM dd')}, 23:59`;
      return `${formatDateTime(timeRange.start, true)} - ${displayEndTime}`;
    }

    return 'Custom';
  }

  return (
    timerangeMap[timeRange.kind] ||
    `${format(new Date(timeRange.start), 'MMM dd')} - ${format(new Date(timeRange.end ?? Date.now()), 'MMM dd')}`
  );
};

export const timerangeMap: { [key in TimeRangeKind]: string | undefined } = {
  today: 'Today',
  yesterday: 'Yesterday',
  '7_days': 'Last 7 days',
  '30_days': 'Last 30 days',
  this_month: 'This month',
  this_quarter: 'This quarter',
  '3_months': 'Three months',
  '6_months': 'Six months',
  '12_months': 'One year',
  q1: 'First quarter',
  q2: 'Second quarter',
  q3: 'Third quarter',
  q4: 'Fourth quarter',
  ytd: 'Year to date',
  custom: undefined,
  this_year: undefined,
  last_month: undefined,
  last_quarter: undefined,
  '3_months_full': undefined,
  '6_months_full': undefined,
  '12_months_full': undefined,
} as const;

export type DatePickerButtonProps = {
  date: Date | undefined;
  setDate: (date: Date | undefined) => void;
  className?: string;
  fromDate?: Date;
  toDate?: Date;
  disabled?: boolean;
  customLabel?: string;
  localizedDateFormat?: boolean;
};

export function DatePickerButton({
  date,
  setDate,
  className,
  fromDate,
  toDate,
  disabled,
  customLabel,
  localizedDateFormat = false,
}: DatePickerButtonProps) {
  const [open, setOpen] = useState(false);
  const displayLabel = customLabel ?? 'Pick a date';
  const formattedDate = date
    ? localizedDateFormat
      ? new Intl.DateTimeFormat(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date)
      : format(date, 'PPP')
    : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={'outline'}
          disabled={disabled}
          className={cn(
            'justify-start rounded-base text-left font-normal',
            !date && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="h-4 w-4" />
          {formattedDate ?? <span>{displayLabel}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          fromDate={fromDate}
          toDate={toDate}
          onSelect={(d) => {
            setDate(d);
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
